import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { HeroStage } from "@/modules/site-layout/ui/landing/hero/hero-section";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/link";

type Props = { icon: LucideIcon; code: string; title: string; text: string; note?: ReactNode; children: ReactNode };

/** What every error page of the site shows: the brand-coloured field of the home page with the code large, a title, one sentence and the buttons. */
export function ErrorPageView({ icon: Icon, code, title, text, note, children }: Props) {
  return (
    <main id="main" className="relative isolate flex flex-1 items-center justify-center px-6 py-20 text-brand-foreground">
      <HeroStage />
      <div className="flex w-full max-w-xl flex-col items-center gap-5 text-center">
        <Icon aria-hidden className="size-10 opacity-80" />
        <p className="text-7xl font-bold tabular-nums tracking-tight drop-shadow-2xl sm:text-8xl">{code}</p>
        <h1 className="text-2xl font-semibold sm:text-3xl">{title}</h1>
        <p className="max-w-md text-base opacity-85">{text}</p>
        {note}
        <div className="mt-2 flex flex-wrap justify-center gap-3">{children}</div>
      </div>
    </main>
  );
}

/** The "go to home" button of the error pages: the home page of the current language. */
export function ErrorHomeButton({ label }: { label: string }) {
  return <Button variant="secondary" nativeButton={false} render={<Link href="/" />}>{label}</Button>;
}
